import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ArrowUpRight, ExternalLink, X } from 'lucide-react';
import './ProjectsSection.css';
import './ProjectsSection.motion.css';
import ImageCarousel from './ImageCarousel';
import useInViewOnce from './hooks/useInViewOnce';
import useCardTilt from './hooks/useCardTilt';
import { REDUCED_MOTION_QUERY } from './hooks/useMotionPrefs';
import { CATEGORIES } from './projects';
import { trackEvent } from './analytics';

const INITIAL_COUNT = 6;
const FILTER_OUT_MS = 130; // fade-out of the current cards before the filtered set comes in
const FILTERS = ['All', ...CATEGORIES];

const formatMonth = (date, lang) =>
    new Date(date).toLocaleDateString(lang, { month: 'short', year: 'numeric' });

// Mini code editor used as the thumbnail for projects without screenshots (e.g. libraries)
const CodeThumb = ({ lines, large = false }) => (
    <div className={`code-thumb ${large ? 'code-thumb--large' : ''}`} dir="ltr" aria-hidden="true">
        <div className="code-thumb-bar">
            <span /><span /><span />
        </div>
        <pre>
            {lines.map((tokens, i) => (
                <code key={i}>
                    {tokens.length === 0 ? ' ' : tokens.map(([kind, text], j) => (
                        <span key={j} className={`tok-${kind}`}>{text}</span>
                    ))}
                </code>
            ))}
        </pre>
    </div>
);

const ProjectModal = ({ project, onClose }) => {
    const { t, i18n } = useTranslation();
    const data = `projects.data.${project.key}`;
    const desc = t(`${data}.description`, { returnObjects: true });
    const start = formatMonth(project.startDate, i18n.language);
    const ongoing = !project.endDate || project.endDate === 'Present';
    // A completed project without a recorded end date shows only its start month, never "– Present"
    let timeline = `${start} – ${formatMonth(project.endDate, i18n.language)}`;
    if (ongoing) {
        timeline = project.status === 'Completed' ? start : `${start} – ${t('projects.modal.present')}`;
    }

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [onClose]);

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div
                className="modal-panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="project-modal-title"
                onClick={e => e.stopPropagation()}
            >
                <button className="modal-close" onClick={onClose} aria-label={t('common.close')}>
                    <X size={18} />
                </button>

                <div className="modal-media" style={{ '--accent': project.accent }}>
                    {project.images.length > 0
                        ? <ImageCarousel images={project.images} interval={5000} />
                        : <CodeThumb lines={project.snippet} large />}
                </div>

                <div className="modal-body">
                    <h2 id="project-modal-title" className="modal-title">{project.title}</h2>
                    <p className="modal-subtitle">{t(`${data}.subtitle`)}</p>

                    <div className="modal-meta">
                        <div>
                            <span>{t('projects.modal.role')}</span>
                            <strong>{t(`${data}.role`)}</strong>
                        </div>
                        <div>
                            <span>{t('projects.modal.timeline')}</span>
                            <strong>{timeline}</strong>
                        </div>
                        <div>
                            <span>{t('projects.modal.status')}</span>
                            <strong className={`status status--${project.status.toLowerCase().replace(' ', '-')}`}>
                                {t(`${data}.status`)}
                            </strong>
                        </div>
                    </div>

                    {Array.isArray(desc) ? (
                        <div className="modal-description">
                            {desc.map(section => (
                                <div key={section.title}>
                                    <h4>{section.title}</h4>
                                    <ul>
                                        {section.items.map(item => <li key={item}>{item}</li>)}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="modal-description">{desc}</p>
                    )}

                    <div className="modal-tech">
                        <h4>{t('projects.modal.tech_personal')}</h4>
                        <div className="modal-tags">
                            {project.technologies.personal.map(tech => <span key={tech} className="tag tag--brand">{tech}</span>)}
                        </div>
                        {project.technologies.contributors.length > 0 && (
                            <>
                                <h4>{t('projects.modal.tech_contributors')}</h4>
                                <div className="modal-tags">
                                    {project.technologies.contributors.map(tech => <span key={tech} className="tag">{tech}</span>)}
                                </div>
                            </>
                        )}
                    </div>

                    {project.link && (
                        <a
                            href={project.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-primary btn-sm"
                            onClick={() => trackEvent('Project Link Click', { project: project.title, location: 'modal' })}
                        >
                            {t('projects.modal.view_project')}
                            <ExternalLink size={16} />
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

// Thumbnail image that fades in once it has actually loaded (no abrupt pop-in)
const ThumbImage = ({ src }) => {
    const [loaded, setLoaded] = useState(false);
    return (
        <img
            src={src}
            alt=""
            loading="lazy"
            className={loaded ? 'is-loaded' : ''}
            ref={el => { if (el && el.complete && el.naturalWidth && !loaded) setLoaded(true); }}
            onLoad={() => setLoaded(true)}
        />
    );
};

const ProjectCard = ({ project, onOpen, index }) => {
    const { t } = useTranslation();
    const label = project.categories.map(c => t(`projects.filters.${c.toLowerCase()}`)).join(' · ');
    // Structured (sectioned) descriptions are too long for a card — fall back to the subtitle
    const description = t(`projects.data.${project.key}.description`, { returnObjects: true });
    const summary = typeof description === 'string' ? description : t(`projects.data.${project.key}.subtitle`);

    return (
        <article className="project-card" style={{ '--accent': project.accent, '--i': Math.min(index, 5) }}>
            <button className="project-thumb" onClick={onOpen} aria-label={`${t('projects.view_project')}: ${project.title}`}>
                {project.images.length > 0
                    ? <ThumbImage src={project.images[0]} />
                    : <CodeThumb lines={project.snippet} />}
                <span className="project-thumb-overlay" aria-hidden="true">
                    <span className="project-thumb-icon"><ArrowUpRight size={16} /></span>
                </span>
            </button>
            <div className="project-body">
                <span className="project-category">{label}</span>
                <div className="project-title-row">
                    <h3 className="project-title">{project.title}</h3>
                    {project.link && (
                        <a
                            href={project.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="project-ext"
                            onClick={() => trackEvent('Project Link Click', { project: project.title, location: 'card' })}
                            aria-label={`${project.title} — ${t('projects.open_link')}`}
                            title={t('projects.open_link')}
                        >
                            <ExternalLink size={14} />
                        </a>
                    )}
                </div>
                <p className="project-description">{summary}</p>
                <div className="project-footer">
                    <div className="project-tags">
                        {project.technologies.personal.slice(0, 3).map(tech => <span key={tech} className="tag">{tech}</span>)}
                    </div>
                    <button className="text-link project-view" onClick={onOpen}>
                        {t('projects.view_project')}
                        <ArrowRight size={13} className="flip-rtl" />
                    </button>
                </div>
            </div>
        </article>
    );
};

const ProjectsSection = ({ projects, activeFilter, setActiveFilter }) => {
    const { t } = useTranslation();
    const sectionRef = useRef(null);
    const gridRef = useRef(null);
    const [showAll, setShowAll] = useState(false);
    const [selected, setSelected] = useState(null);
    useInViewOnce(sectionRef);
    useCardTilt(gridRef, { selector: '.project-card', max: 1.5 });

    // The grid lags the active filter by a short fade-out, then the new set animates in.
    // (The filter can also change from outside, e.g. a Services card, so this follows the prop.)
    // State goes to data-* attributes so React never overwrites the observer's `is-inview` class.
    const [shownFilter, setShownFilter] = useState(activeFilter);
    const [leaving, setLeaving] = useState(false);
    const [swapCount, setSwapCount] = useState(0);
    useEffect(() => {
        if (activeFilter === shownFilter) return undefined;
        const reduced = window.matchMedia && window.matchMedia(REDUCED_MOTION_QUERY).matches;
        if (reduced) {
            setShownFilter(activeFilter);
            setSwapCount(n => n + 1);
            return undefined;
        }
        setLeaving(true);
        const timer = setTimeout(() => {
            setShownFilter(activeFilter);
            setSwapCount(n => n + 1);
            setLeaving(false);
        }, FILTER_OUT_MS);
        return () => clearTimeout(timer);
    }, [activeFilter, shownFilter]);

    const filtered = shownFilter === 'All'
        ? projects
        : projects.filter(p => p.categories.includes(shownFilter));
    const visible = showAll ? filtered : filtered.slice(0, INITIAL_COUNT);

    return (
        <section className="section projects" id="Projects" ref={sectionRef}>
            <div className="container">
                <div className="section-header-row pj-header" data-inview>
                    <div className="section-header">
                        <span className="eyebrow">{t('projects.eyebrow')}</span>
                        <h2 className="section-title">{t('projects.title')}</h2>
                        <p className="section-subtitle">{t('projects.subtitle')}</p>
                    </div>
                    <div className="projects-controls">
                        <div className="project-filters" role="tablist" aria-label={t('projects.filter_label')}>
                            {FILTERS.map(key => (
                                <button
                                    key={key}
                                    role="tab"
                                    aria-selected={activeFilter === key}
                                    className={`filter-chip ${activeFilter === key ? 'active' : ''}`}
                                    onClick={() => {
                                        if (key !== activeFilter) trackEvent('Project Filter', { category: key });
                                        setActiveFilter(key);
                                    }}
                                >
                                    {t(`projects.filters.${key.toLowerCase()}`)}
                                </button>
                            ))}
                        </div>
                        {filtered.length > INITIAL_COUNT && (
                            <button className="text-link" onClick={() => setShowAll(v => !v)}>
                                {showAll ? t('projects.show_less') : t('projects.view_all', { count: filtered.length })}
                                <ArrowRight size={14} className="flip-rtl" />
                            </button>
                        )}
                    </div>
                </div>

                <div
                    className="projects-grid"
                    data-leaving={leaving || undefined}
                    data-swapped={swapCount > 0 || undefined}
                    ref={gridRef}
                    data-inview
                >
                    {visible.map((project, index) => (
                        <ProjectCard
                            key={`${swapCount}-${project.key}`}
                            project={project}
                            index={index}
                            onOpen={() => {
                                setSelected(project);
                                trackEvent('Project Opened', { project: project.title });
                            }}
                        />
                    ))}
                </div>
            </div>

            {selected && <ProjectModal project={selected} onClose={() => setSelected(null)} />}
        </section>
    );
};

export default ProjectsSection;
